import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  IconButton,
  useTheme,
  Typography,
  Button,
  Stack,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Badge,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ColorModeContext } from "../../theme";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { logoutUser } from "../../redux/actions/authActions";
import { getUserUnreadCount } from "../../redux/actions/notificationActions";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LoginIcon from "@mui/icons-material/Login";
import BusinessIcon from "@mui/icons-material/Business";
import MenuIcon from "@mui/icons-material/Menu";
import Logout from "@mui/icons-material/Logout";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { castEnumForDisplay } from "../../helpers/enumHelpers";
import ConfirmDialog from "../common/ConfirmDialog";
import NotificationUserModal from "../notifications/NotificationUserModal";

export interface TopBarNavigationProps {
  onMobileNavOpen: () => void;
}

const TopBarNavigation: React.FC<TopBarNavigationProps> = ({
  onMobileNavOpen,
}) => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { loadingSchool, errorLoadingSchool, school } = useAppSelector(
    (state) => state.school,
  );
  const { userInfo } = useAppSelector((state) => state.auth); // Adjusted from state.user
  const { unreadCount } = useAppSelector((state) => state.notification);

  const [isNotifModalOpen, setIsNotifModalOpen] = useState<boolean>(false);
  const [time, setTime] = useState<Date>(new Date());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState<boolean>(false);

  const openMenu = Boolean(anchorEl);

  const padZero = (num: number): string => (num < 10 ? "0" : "") + num;

  const formatTime = (): string => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();
    const meridium = hours >= 12 ? "PM" : "AM";
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)} ${meridium}`;
  };

  useEffect(() => {
    const intervalId = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (userInfo) {
      dispatch(getUserUnreadCount());
    }
  }, [dispatch, userInfo]);

  const currentDate = new Date().toDateString();

  // Menu Handlers
  const handleProfileClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = (): void => {
    setAnchorEl(null);
  };

  const handleLogoutClick = (): void => {
    handleProfileClose();
    setIsLogoutDialogOpen(true);
  };

  const handleConfirmLogout = (): void => {
    dispatch(logoutUser());
    navigate("/auth");
  };

  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: "relative",
        zIndex: theme.appZIndex.sticky,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          minHeight: theme.appSizes?.headerHeight || 64,
          gap: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ flex: 1, minWidth: 0, alignItems: "center" }}
        >
          {userInfo && (
            <IconButton
              onClick={onMobileNavOpen}
              edge="start"
              sx={{
                display: { lg: "none" },
                color: "text.secondary",
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h4"
              noWrap
              sx={{
                fontSize: {
                  xs: "1rem",
                  sm: "1.25rem",
                  md: theme.typography.h4.fontSize,
                },
                fontWeight: 700,
                color: errorLoadingSchool ? "error.main" : "text.primary",
              }}
            >
              {loadingSchool
                ? "Loading..."
                : errorLoadingSchool ||
                  (school && school.name) ||
                  "School Name"}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
            pl: 3,
            pr: 0.5,
            py: 0.5,
            borderRadius: "50px",
            bgcolor: alpha(theme.palette.neutral?.main || "#ccc", 0.1),
            border: `1px solid ${theme.palette.divider}`,
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {currentDate}
          </Typography>

          <Box
            sx={{
              background: theme.appGradients?.info || theme.palette.info.main,
              px: 2,
              py: 0.5,
              borderRadius: "50px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "#fff", fontWeight: 700 }}
            >
              {formatTime()}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={{ xs: 0.5, sm: 1 }}
          sx={{ flex: 1, alignItems: "center", justifyContent: "flex-end" }}
        >
          <IconButton
            onClick={colorMode.toggleColorMode}
            color="inherit"
            size="small"
          >
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlinedIcon />
            ) : (
              <LightModeOutlinedIcon />
            )}
          </IconButton>

          {userInfo ? (
            <>
              <IconButton
                color="inherit"
                onClick={() => setIsNotifModalOpen(true)}
              >
                <Badge badgeContent={unreadCount} color="error" showZero>
                  <NotificationsOutlinedIcon />
                </Badge>
              </IconButton>

              <IconButton
                onClick={handleProfileClick}
                color={openMenu ? "primary" : "default"}
                aria-controls={openMenu ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={openMenu ? "true" : undefined}
              >
                <PersonOutlinedIcon />
              </IconButton>
            </>
          ) : (
            <Button
              variant="gradient"
              color="info"
              onClick={() => navigate("/auth")}
              sx={{
                ml: { xs: 1, md: 2 },
                px: { xs: 2, md: 4 },
                minWidth: { xs: "auto", md: 64 },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", md: "block" } }}
              >
                Login
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "block", md: "none" } }}
              >
                <LoginIcon fontSize="small" />
              </Box>
            </Button>
          )}
        </Stack>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openMenu}
        onClose={handleProfileClose}
        onClick={handleProfileClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              borderRadius: theme.appRadius?.md || 2,
              minWidth: 180,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {userInfo?.firstName || "User"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {userInfo ? castEnumForDisplay(userInfo.userRole) : "Staff"}
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={() => navigate("/profile")}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem onClick={() => navigate("/school")}>
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          School details
        </MenuItem>
        <MenuItem onClick={() => navigate("/settings")}>
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogoutClick} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <Logout fontSize="small" color="error" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <NotificationUserModal
        open={isNotifModalOpen}
        onClose={() => setIsNotifModalOpen(false)}
      />

      <ConfirmDialog
        open={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Log Out"
        description="Are you sure you want to end your session?"
        severity="error"
        confirmText="Yes, Log Out"
      />
    </Box>
  );
};

export default TopBarNavigation;
